// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Search, Users, CheckSquare, Target, FileText, ArrowRight, Calendar, Folder, UserPlus, X, Loader2, CornerDownLeft, ArrowUpDown, Hash
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { PillTabs } from "@/components/common";

const CATEGORIES = [
  { key: "clients", label: "Clients", icon: Users, color: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-200" },
  { key: "tasks", label: "Tasks", icon: CheckSquare, color: "text-purple-600", bg: "bg-purple-50", ring: "ring-purple-200" },
  { key: "goals", label: "Goals", icon: Target, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  { key: "notes", label: "Notes", icon: FileText, color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-200" },
  { key: "cases", label: "Cases", icon: Folder, color: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-200" },
];

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  completed: "bg-blue-100 text-blue-700 border-blue-200",
  done: "bg-blue-100 text-blue-700 border-blue-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  todo: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-indigo-100 text-indigo-700 border-indigo-200",
  lead: "bg-violet-100 text-violet-700 border-violet-200",
  prospect: "bg-cyan-100 text-cyan-700 border-cyan-200",
  open: "bg-sky-100 text-sky-700 border-sky-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

function getStatusClass(status: string) {
  return STATUS_COLORS[status?.toLowerCase()] || "bg-slate-100 text-slate-600 border-slate-200";
}

function HighlightText({ text, highlight }: { text: string; highlight: string }) {
  if (!highlight || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(highlight.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-100 text-yellow-900 rounded-sm px-0.5">{text.slice(idx, idx + highlight.length)}</mark>
      {text.slice(idx + highlight.length)}
    </>
  );
}

function ResultItem({ icon: Icon, iconColor, iconBg, title, subtitle, badge, onClick, isActive, highlight }) {
  return (
    <button
      onClick={onClick}
      data-search-result
      className={`w-full flex items-center gap-2.5 px-4 py-2 transition-all text-left group outline-none ${
        isActive
          ? "bg-blue-50/80"
          : "hover:bg-slate-50"
      }`}
    >
      <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 truncate">
          <HighlightText text={title || ""} highlight={highlight} />
        </p>
        {subtitle && (
          <p className="text-[11px] text-slate-400 truncate">
            <HighlightText text={subtitle || ""} highlight={highlight} />
          </p>
        )}
      </div>
      {badge && (
        <span className={`text-[10px] font-medium capitalize px-1.5 py-0.5 rounded-md shrink-0 ${getStatusClass(badge)}`}>
          {badge?.replace(/_/g, " ")}
        </span>
      )}
      <ArrowRight className={`w-3 h-3 shrink-0 transition-all ${
        isActive ? "text-blue-500 translate-x-0.5" : "text-slate-200 group-hover:text-slate-400"
      }`} />
    </button>
  );
}

function CategorySection({ label, icon: Icon, color, bg, results, onClose, navigate, activeIndex, startIndex, highlight }) {
  if (!results || results.length === 0) return null;
  return (
    <div className="mb-0.5">
      <div className="flex items-center gap-2 px-4 py-1.5">
        <Icon className={`w-3 h-3 ${color}`} />
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</span>
        <div className="flex-1 h-px bg-slate-100 ml-1" />
        <span className="text-[10px] text-slate-400">{results.length}</span>
      </div>
      <div>
        {results.slice(0, 5).map((item, i) => (
          <ResultItem
            key={item.id}
            {...item}
            highlight={highlight}
            isActive={startIndex + i === activeIndex}
            onClick={() => { item.onClick(); onClose(); }}
          />
        ))}
      </div>
    </div>
  );
}

export default function GlobalSearchModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  const debounceSearch = useCallback(debounce((val) => setDebouncedQuery(val), 300), []);

  useEffect(() => {
    debounceSearch(query);
    setActiveIndex(-1);
  }, [query]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setDebouncedQuery("");
      setActiveCategory("all");
      setActiveIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const { data: clients = [], isLoading: loadingClients } = useQuery({
    queryKey: ["search-clients"],
    queryFn: () => api.entities.Client.list(),
    enabled: isOpen,
    staleTime: 60000,
  });
  const { data: tasks = [], isLoading: loadingTasks } = useQuery({
    queryKey: ["search-tasks"],
    queryFn: () => api.entities.Task.list("-created_date", 200),
    enabled: isOpen,
    staleTime: 60000,
  });
  const { data: goals = [], isLoading: loadingGoals } = useQuery({
    queryKey: ["search-goals"],
    queryFn: () => api.entities.Goal.list("-created_date", 200),
    enabled: isOpen,
    staleTime: 60000,
  });
  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ["search-notes"],
    queryFn: () => api.entities.Note.list("-created_date", 200),
    enabled: isOpen,
    staleTime: 60000,
  });
  const { data: cases = [], isLoading: loadingCases } = useQuery({
    queryKey: ["search-cases"],
    queryFn: () => api.entities.Case.list("-created_date", 200),
    enabled: isOpen,
    staleTime: 60000,
  });

  const isLoading = loadingClients || loadingTasks || loadingGoals || loadingNotes || loadingCases;

  const q = debouncedQuery.toLowerCase().trim();

  const matchedClients = q
    ? clients.filter(c =>
        (
          c.full_name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone?.toLowerCase().includes(q)
        )
      ).map(c => ({
        id: c.id,
        icon: Users, iconColor: "text-blue-600", iconBg: "bg-blue-50",
        title: c.full_name || c.name,
        subtitle: c.email,
        badge: c.status,
        onClick: () => navigate(createPageUrl("ClientDetail") + `?clientId=${c.id}`),
      }))
    : [];

  const matchedTasks = q
    ? tasks.filter(t =>
        t.title?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.client_name?.toLowerCase().includes(q)
      ).map(t => ({
        id: t.id,
        icon: CheckSquare, iconColor: "text-purple-600", iconBg: "bg-purple-50",
        title: t.title,
        subtitle: t.client_name ? `Client: ${t.client_name}` : t.description,
        badge: t.status,
        onClick: () => navigate(createPageUrl("Tasks")),
      }))
    : [];

  const matchedGoals = q
    ? goals.filter(g =>
        g.title?.toLowerCase().includes(q) ||
        g.description?.toLowerCase().includes(q) ||
        g.client_name?.toLowerCase().includes(q)
      ).map(g => ({
        id: g.id,
        icon: Target, iconColor: "text-emerald-600", iconBg: "bg-emerald-50",
        title: g.title,
        subtitle: g.client_name ? `Client: ${g.client_name}` : g.description,
        badge: g.status,
        onClick: () => navigate(createPageUrl("Goals")),
      }))
    : [];

  const matchedNotes = q
    ? notes.filter(n =>
        n.content?.toLowerCase().includes(q) ||
        n.client_name?.toLowerCase().includes(q)
      ).map(n => ({
        id: n.id,
        icon: FileText, iconColor: "text-amber-600", iconBg: "bg-amber-50",
        title: n.content?.slice(0, 80) || "Note",
        subtitle: n.client_name ? `Client: ${n.client_name}` : undefined,
        badge: n.note_type,
        onClick: () => n.client_id
          ? navigate(createPageUrl("ClientDetail") + `?clientId=${n.client_id}&tab=notes`)
          : null,
      }))
    : [];

  const matchedCases = q
    ? cases.filter(c =>
        c.title?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.client_name?.toLowerCase().includes(q)
      ).map(c => ({
        id: c.id,
        icon: Folder, iconColor: "text-rose-600", iconBg: "bg-rose-50",
        title: c.title,
        subtitle: c.client_name ? `Client: ${c.client_name}` : c.description,
        badge: c.status,
        onClick: () => navigate(createPageUrl("Cases")),
      }))
    : [];

  const totalResults = matchedClients.length + matchedTasks.length + matchedGoals.length + matchedNotes.length + matchedCases.length;

  const categoryMap = {
    clients: { label: "Clients", icon: Users, color: "text-blue-600", bg: "bg-blue-50", results: matchedClients },
    tasks: { label: "Tasks", icon: CheckSquare, color: "text-purple-600", bg: "bg-purple-50", results: matchedTasks },
    goals: { label: "Goals", icon: Target, color: "text-emerald-600", bg: "bg-emerald-50", results: matchedGoals },
    notes: { label: "Notes", icon: FileText, color: "text-amber-600", bg: "bg-amber-50", results: matchedNotes },
    cases: { label: "Cases", icon: Folder, color: "text-rose-600", bg: "bg-rose-50", results: matchedCases },
  };

  // Build flat list of visible results for keyboard navigation
  const visibleCategories = CATEGORIES.filter(cat => activeCategory === "all" || activeCategory === cat.key);
  const allVisibleResults: any[] = [];
  visibleCategories.forEach(cat => {
    const items = categoryMap[cat.key]?.results || [];
    items.slice(0, 5).forEach(item => allVisibleResults.push(item));
  });

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, allVisibleResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, -1));
      } else if (e.key === "Enter" && activeIndex >= 0 && activeIndex < allVisibleResults.length) {
        e.preventDefault();
        allVisibleResults[activeIndex]?.onClick();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, activeIndex, allVisibleResults]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && resultsRef.current) {
      const items = resultsRef.current.querySelectorAll("[data-search-result]");
      items[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // Compute startIndex for each category
  const categoryStartIndices: Record<string, number> = {};
  let runningIndex = 0;
  visibleCategories.forEach(cat => {
    categoryStartIndices[cat.key] = runningIndex;
    runningIndex += Math.min(categoryMap[cat.key]?.results?.length || 0, 5);
  });

  const quickLinks = [
    { label: "New Client", icon: UserPlus, desc: "Add a client", onClick: () => { navigate(createPageUrl("Clients")); onClose(); } },
    { label: "Calendar", icon: Calendar, desc: "View schedule", onClick: () => { navigate(createPageUrl("Calendar")); onClose(); } },
    { label: "Tasks", icon: CheckSquare, desc: "Manage tasks", onClick: () => { navigate(createPageUrl("Tasks")); onClose(); } },
    { label: "Goals", icon: Target, desc: "Track goals", onClick: () => { navigate(createPageUrl("Goals")); onClose(); } },
    { label: "Cases", icon: Folder, desc: "Open cases", onClick: () => { navigate(createPageUrl("Cases")); onClose(); } },
    { label: "Documents", icon: FileText, desc: "Signing & docs", onClick: () => { navigate(createPageUrl("DocumentSigning")); onClose(); } },
  ];

  const recentSearchHints = ["client name", "task title", "goal", "note content", "case"];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="p-0 max-w-[720px] overflow-hidden gap-0 rounded-2xl border border-slate-200/60 shadow-2xl shadow-slate-900/10 backdrop-blur-xl bg-white [&>button:last-child]:hidden" style={{ maxHeight: "85vh" }}>
        <VisuallyHidden><DialogTitle>Search</DialogTitle></VisuallyHidden>
        {/* Search Header */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex h-10 items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 ring-1 ring-slate-200/80 transition-all focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-200">
            {isLoading && q ? (
              <Loader2 className="w-5 h-5 text-blue-500 shrink-0 animate-spin" />
            ) : (
              <Search className="w-5 h-5 text-slate-400 shrink-0" />
            )}
            <input
              ref={inputRef}
              type="text"
              placeholder="Search clients, tasks, goals, notes, cases..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="global-search-input h-full flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-slate-900 shadow-none outline-none ring-0 placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
              style={{ border: 0, outline: "none", boxShadow: "none" }}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="shrink-0 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                title="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="shrink-0 text-[10px] bg-white text-slate-400 px-1.5 py-0.5 rounded-md font-mono border border-slate-200/80 shadow-sm">esc</kbd>
          </div>
        </div>

        {/* Results Body */}
        <div ref={resultsRef} className="overflow-y-auto border-t border-slate-100" style={{ maxHeight: "calc(85vh - 120px)" }}>
          {/* Empty state - Quick Navigation */}
          {!q && (
            <div className="px-5 py-5">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Quick Navigation</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {quickLinks.map(link => {
                  const Icon = link.icon;
                  return (
                    <button
                      key={link.label}
                      onClick={link.onClick}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all group"
                    >
                      <div className="w-10 h-10 bg-slate-50 group-hover:bg-blue-100 rounded-xl flex items-center justify-center transition-colors">
                        <Icon className="w-5 h-5 text-slate-500 group-hover:text-blue-600 transition-colors" />
                      </div>
                      <span className="text-[11px] font-medium text-slate-600 group-hover:text-blue-700 transition-colors">{link.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2.5">Try searching for</p>
                <div className="flex flex-wrap gap-2">
                  {recentSearchHints.map(hint => (
                    <button
                      key={hint}
                      onClick={() => setQuery(hint)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                    >
                      <Hash className="w-3 h-3 text-slate-400" />
                      {hint}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Category filter pills */}
          {q && totalResults > 0 && (
            <div className="px-4 py-2 border-b border-slate-100">
              <PillTabs
                tabs={[
                  { value: "all", label: `All (${totalResults})` },
                  ...CATEGORIES
                    .filter(cat => (categoryMap[cat.key]?.results?.length || 0) > 0)
                    .map(cat => ({
                      value: cat.key,
                      label: `${cat.label} (${categoryMap[cat.key]?.results?.length || 0})`,
                      icon: cat.icon,
                    })),
                ]}
                value={activeCategory}
                onChange={(val) => { setActiveCategory(val); setActiveIndex(-1); }}
                variant="solid"
                size="sm"
                className="gap-1"
              />
            </div>
          )}

          {/* Loading state */}
          {q && isLoading && totalResults === 0 && (
            <div className="text-center py-12 px-4">
              <Loader2 className="w-8 h-8 text-blue-400 mx-auto mb-2.5 animate-spin" />
              <p className="text-slate-500 text-xs">Searching...</p>
            </div>
          )}

          {/* Empty state */}
          {q && !isLoading && totalResults === 0 && (
            <div className="text-center py-12 px-4">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Search className="w-5 h-5 text-slate-300" />
              </div>
              <p className="text-slate-700 font-medium text-sm">No results for "{debouncedQuery}"</p>
              <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
            </div>
          )}

          {/* Results grouped by category */}
          {q && totalResults > 0 && (
            <div className="py-2">
              {visibleCategories.map(cat => (
                <CategorySection
                  key={cat.key}
                  label={cat.label}
                  icon={cat.icon}
                  color={cat.color}
                  bg={cat.bg}
                  results={categoryMap[cat.key]?.results}
                  onClose={onClose}
                  navigate={navigate}
                  activeIndex={activeIndex}
                  startIndex={categoryStartIndices[cat.key]}
                  highlight={q}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-2.5 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-3 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono shadow-sm">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono shadow-sm">↓</kbd>
              <span className="ml-0.5">navigate</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono shadow-sm">↵</kbd>
              <span className="ml-0.5">open</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono shadow-sm">esc</kbd>
              <span className="ml-0.5">close</span>
            </span>
          </div>
          {q && totalResults > 0 && (
            <span className="text-[11px] text-slate-400 font-medium">{totalResults} result{totalResults !== 1 ? 's' : ''}</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}